/**
 * IDE Sync Subcommand
 * Sync .jai1 content to IDE directories (rules, workflows, commands, skills)
 */
import { Command } from 'commander';
import { checkbox, confirm } from '@inquirer/prompts';
import type { MigrateIDE, MigrateContentType } from '../../types/migrate-ide.types.js';
import { MigrateIdeService } from '../../services/migrate-ide.service.js';
import { IDE_MIGRATION_CONFIGS, getMigrationIDEs } from '../../constants/ide-migration-configs.js';
import { checkboxTheme } from '../../utils/prompt-theme.js';

/**
 * Create sync subcommand for IDE group
 * Syncs .jai1 content to IDE-specific directories
 */
export function createSyncSubcommand(): Command {
    const cmd = new Command('sync')
        .description('Sync .jai1 content to IDE directories (Cursor, Windsurf, Claude Code, Codex, etc.)')
        .option('--ides <ides...>', 'Target IDEs (cursor, windsurf, antigravity, claude, opencode, codex)')
        .option('--type <types...>', 'Content types (rules, workflows, commands, skills)')
        .option('--dry-run', 'Preview changes without writing files')
        .option('-y, --yes', 'Headless mode (skip all prompts)')
        .option('--all', 'Select all available IDEs')
        .action(async (options: {
            ides?: string[];
            type?: string[];
            dryRun?: boolean;
            yes?: boolean;
            all?: boolean;
        }) => {
            await runSync(options);
        });

    return cmd;
}

export async function runSync(options: {
    ides?: string[];
    type?: string[];
    dryRun?: boolean;
    yes?: boolean;
    all?: boolean;
}) {
    const service = new MigrateIdeService();
    const headless = options.yes === true;

    console.log('\n🔄 Sync rules, workflows and skills to IDE(s)\n');

    // Scan .jai1 content
    console.log('📁 Scanning .jai1/ directory...');
    console.log('   • .jai1/rule-preset/ (active rule preset)');
    console.log('   • .jai1/rules/ (manual rules)');
    console.log('   • .jai1/workflows/');
    console.log('   • .jai1/skills/\n');

    const content = await service.scanJai1Content();

    if (content.totalCount === 0) {
        console.log('⚠️  No content found in .jai1/');
        console.log('   💡 Run "jai1 rules apply" to apply a rule preset, or');
        console.log('   💡 Create content in .jai1/rules/, .jai1/workflows/ or .jai1/skills/\n');
        process.exit(1);
    }

    console.log(`✓ Found: ${content.rules.length} rules, ${content.workflows.length} workflows, ${content.skills.length} skills\n`);

    // IDE alias map for backward compatibility
    const ideAliases: Record<string, MigrateIDE> = {
        'claudecode': 'claude',
        'codexcli': 'codex',
        'openai-codex': 'codex',
    };
    const normalizeIde = (ide: string): MigrateIDE => (ideAliases[ide] ?? ide) as MigrateIDE;

    // Select IDEs
    let selectedIdes: MigrateIDE[];
    if (options.all) {
        // --all flag: select all available IDEs
        selectedIdes = getMigrationIDEs();
    } else if (options.ides && options.ides.length > 0) {
        selectedIdes = options.ides.map(normalizeIde);
    } else if (headless) {
        throw new Error(
            'Headless mode (-y) requires --ides or --all flag.\n' +
            'Examples:\n' +
            '  j ide sync --ides cursor windsurf -y\n' +
            '  j ide sync --all -y'
        );
    } else {
        const ideChoices = getMigrationIDEs().map(ide => {
            const config = IDE_MIGRATION_CONFIGS[ide];
            return {
                name: `${config.icon} ${config.name}`,
                value: ide,
            };
        });

        selectedIdes = await checkbox({
            message: 'Select target IDE(s) (SPACE to select, ENTER to confirm):',
            choices: ideChoices,
            theme: checkboxTheme,
        });

        if (selectedIdes.length === 0) {
            console.log('\n⚠️  No IDE selected!');
            console.log('   💡 Select at least 1 IDE.\n');
            process.exit(0);
        }
    }

    // Select content types
    let selectedTypes: MigrateContentType[];
    if (options.type && options.type.length > 0) {
        selectedTypes = options.type as MigrateContentType[];
    } else if (headless) {
        // In headless mode, select all content types
        selectedTypes = ['rules', 'workflows', 'commands', 'skills'];
    } else {
        const typeChoices = [
            { name: `Rules (${content.rules.length} files)`, value: 'rules' as MigrateContentType },
            { name: `Workflows (${content.workflows.length} files)`, value: 'workflows' as MigrateContentType },
            { name: `Commands (${content.commands.length} files)`, value: 'commands' as MigrateContentType },
            { name: `Skills (${content.skills.length} folders)`, value: 'skills' as MigrateContentType },
        ];

        selectedTypes = await checkbox({
            message: 'Select content types to sync:',
            choices: typeChoices,
            theme: checkboxTheme,
        });

        if (selectedTypes.length === 0) {
            console.log('\n⚠️  No content type selected!');
            console.log('   💡 Select at least 1 content type.\n');
            process.exit(0);
        }
    }

    // Calculate total files
    const totalItems = selectedTypes.reduce((sum, type) => {
        return sum + (type === 'rules' ? content.rules.length :
            type === 'workflows' ? content.workflows.length :
                type === 'commands' ? content.commands.length :
                    content.skills.length);
    }, 0);
    const totalFiles = totalItems * selectedIdes.length;

    // Preview
    console.log('\n📊 Preview:\n');
    console.log(`   IDEs: ${selectedIdes.map(ide => IDE_MIGRATION_CONFIGS[ide].name).join(', ')}`);
    console.log(`   Content types: ${selectedTypes.join(', ')}`);
    console.log(`   Planned target operations: ${totalFiles} items\n`);

    if (options.dryRun) {
        console.log('🔍 DRY RUN - No files will be written\n');
        return;
    }

    // Confirm (skip in headless mode)
    if (!headless) {
        const confirmed = await confirm({
            message: 'Proceed with sync?',
            default: true,
        });

        if (!confirmed) {
            console.log('\n❌ Sync cancelled.\n');
            process.exit(0);
        }
    }

    // Migrate
    console.log('\n🔄 Syncing...\n');
    let completed = 0;

    const results = await service.migrate(
        selectedIdes,
        selectedTypes,
        content,
        (result) => {
            completed++;
            const icon = result.status === 'created' ? '✓' :
                result.status === 'updated' ? '↻' :
                    result.status === 'error' ? '✗' : '○';

            const percentage = Math.round((completed / totalFiles) * 100);
            console.log(`   ${icon} [${percentage}%] ${result.targetPath}`);

            if (result.status === 'error' && result.error) {
                console.log(`      Error: ${result.error}`);
            }
        }
    );

    // Summary
    const created = results.filter(r => r.status === 'created').length;
    const updated = results.filter(r => r.status === 'updated').length;
    const skipped = results.filter(r => r.status === 'skipped').length;
    const errors = results.filter(r => r.status === 'error').length;

    console.log('\n✅ Sync complete!\n');
    console.log(`   Created: ${created}`);
    console.log(`   Updated: ${updated}`);
    if (skipped > 0) console.log(`   Skipped: ${skipped}`);
    if (errors > 0) console.log(`   Errors: ${errors}`);
    console.log('');
}
